import default_img from "./assets/default_img.png";

import {auth, db, storage} from "./firebase";
import {signInWithEmailAndPassword, signOut} from "firebase/auth";
import {getDownloadURL, listAll, ref, uploadBytes} from "firebase/storage";
import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	query,
	updateDoc,
	where,
	writeBatch
} from "firebase/firestore";
import {LEGACY_ALL_PROFILE_ID, VIRTUAL_ALL_PROFILE_ID, getFormattedValue} from "./tools";

const EMULATOR_PROJECT_ID = 'demo-test';
const RESERVED_PROFILE_NAME = "ALL";

const normalizeProfileId = (profileId) => (
	profileId === LEGACY_ALL_PROFILE_ID ? VIRTUAL_ALL_PROFILE_ID : profileId
);

const toFiniteNumber = (value, fallback = 0) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
};

const getProfileIds = (entity) => {
	if (Array.isArray(entity?.profileIds)) {
		return Array.from(new Set(entity.profileIds.map(normalizeProfileId).filter(Boolean)));
	}
	if (entity?.profileId) return [normalizeProfileId(entity.profileId)];
	return [];
};

const sanitizeIdxPerProfile = (idxPerProfile) => {
	const normalizedIdxPerProfile = {};
	if (!idxPerProfile || typeof idxPerProfile !== "object") return normalizedIdxPerProfile;

	Object.entries(idxPerProfile).forEach(([profileId, idx]) => {
		const normalizedProfileId = normalizeProfileId(profileId);
		if (!normalizedProfileId) return;
		normalizedIdxPerProfile[normalizedProfileId] = toFiniteNumber(idx);
	});

	return normalizedIdxPerProfile;
};

const buildCategoryPayload = (categoryObject) => {
	const profileIds = Array.from(new Set(getProfileIds(categoryObject)))
		.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);
	const idxPerProfile = sanitizeIdxPerProfile(categoryObject?.idxPerProfile);
	delete idxPerProfile[VIRTUAL_ALL_PROFILE_ID];

	const categoryName = String(categoryObject?.category ?? "").trim();
	return {
		category: getFormattedValue(categoryName),
		idx: toFiniteNumber(categoryObject?.idx),
		profileIds,
		idxPerProfile
	};
};

const toArray = (querySnapshot) => querySnapshot.docs.map((snapshot) => ({
	id: snapshot.id,
	...snapshot.data(),
}));

export const isDestructiveCleanupAllowed = () => (
	process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true'
	&& process.env.REACT_APP_ALLOW_DB_CLEANUP === 'true'
	&& process.env.REACT_APP_FIREBASE_PROJECT_ID === EMULATOR_PROJECT_ID
);

const fetchLinkRef = async (linkId, addError) => {
	try {
		return doc(db, 'links', linkId);
	} catch (error) {
		addError(`Error while fetching Link ref from Firebase: ${error.message}`);
	}
}

const fetchCategoryRef = async (categoryId, addError) => {
	try {
		return doc(db, 'categories', categoryId);
	} catch (error) {
		addError(`Error while fetching Category ref in Firebase: ${error.message}`);
	}
}

export const login = async (email, password, addError) => {
	try {
		return await signInWithEmailAndPassword(auth, email, password);
	} catch (error) {
		addError(
			error.code === 'auth/invalid-credential'
				? 'Invalid email or password'
				: 'An error occurred during login'
		);
	}
}

export const logout = async (addError) => {
	try {
		await signOut(auth);
		return true;
	} catch (error) {
		addError(`Error while logging out: ${error.message}`);
	}
}

export const fetchLinks = async (addError) => {
	try {
		const querySnapshot = await getDocs(collection(db, 'links'));
		return toArray(querySnapshot);
	} catch (error) {
		addError(`Error while getting Links from Firebase: ${error.message}`)
		return [];
	}
}

export const updateLink = async (linkId, linkData, addError) => {
	try {
		const linkRef = await fetchLinkRef(linkId, addError);
		await updateDoc(linkRef, linkData);
	} catch (error) {
		addError(`Error while updating Link in Firebase: ${error.message}`);
	}
};

export const uploadDefaultImage = async (addError) => {
	try {
		const response = await fetch(default_img);
		const blob = await response.blob();
		const defaultImageName = 'default_image.png';
		const storageRef = ref(storage, `image_link/${defaultImageName}`);
		await uploadBytes(storageRef, blob);
		return await getDownloadURL(storageRef);
	} catch (error) {
		addError(`Error while uploading default Image to Firebase: ${error.message}`)
	}
};

export const uploadNewImage = async (image, addError) => {
	try {
		const storageRef = ref(storage, `image_link/${image.name}`);
		const snapshot = await uploadBytes(storageRef, image);
		return await getDownloadURL(snapshot.ref);
	} catch (error) {
		addError(`Error while uploading new Image to Firebase: ${error.message}`)
	}
}

export const fetchAllStorageImages = async (addError) => {
	try {
		const listRef = ref(storage, 'image_link');
		const result = await listAll(listRef);
		
		const urlPromises = result.items.map(async (itemRef) => {
			try {
				return await getDownloadURL(itemRef);
			} catch {
				return null;
			}
		});
		
		const urls = await Promise.all(urlPromises);
		return urls.filter(url => url !== null);
	} catch (error) {
		addError(`Error while fetching images from Storage: ${error.message}`);
		return [];
	}
}

export const addLink = async (link, addError) => {
	try {
		const payload = {
			...link,
			profileIds: Array.from(new Set(getProfileIds(link)))
				.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID),
		};
		delete payload.profileId;
		return await addDoc(collection(db, 'links'), payload);
	} catch (error) {
		addError(`Error while adding new Link to Firebase: ${error.message}`)
	}
}

export const deleteLink = async (linkId, addError) => {
	const linkRef = await fetchLinkRef(linkId, addError);
	try {
		await deleteDoc(linkRef);
		return true;
	} catch (error) {
		addError(`Error while removing Link from Firebase: ${error.message}`);
		return false;
	}
}

export const fetchLinksByCategory = async (category, addError) => {
	try {
		const querySnapshot = await getDocs(query(collection(db, 'links'), where('category', '==', category)));
		return toArray(querySnapshot);
	} catch (error) {
		addError(`Error while fetching Links by Category from Firebase: ${error.message}`);
		return [];
	}
};

export const fetchCategories = async (addError) => {
	try {
		const querySnapshot = await getDocs(collection(db, 'categories'));
		return toArray(querySnapshot);
	} catch (error) {
		addError(`Error while getting Categories from Firebase: ${error.message}`);
		return [];
	}
}

export const fetchCategory = async (categoryId, addError) => {
	try {
		const ref = await fetchCategoryRef(categoryId, addError);
		const snapshot = await getDoc(ref);
		if (!snapshot.exists()) return null;
		return {id: snapshot.id, ...snapshot.data()};
	} catch (error) {
		addError(`Error while fetching Category in Firebase: ${error.message}`);
		return null;
	}
}

export const updateCategory = async (categoryObject, addError) => {
	if (!categoryObject?.id) {
		addError('Invalid category ID for update');
		return false;
	}

	const categoryData = buildCategoryPayload(categoryObject);

	const categoryRef = await fetchCategoryRef(categoryObject.id, addError);
	try {
		await updateDoc(categoryRef, categoryData);
		return true;
	} catch (error) {
		addError(`Error while updating Category in Firebase: ${error.message}`);
		return false;
	}
};

export const addCategory = async (category, addError) => {
	try {
		const categoryData = buildCategoryPayload(category);
		return await addDoc(collection(db, "categories"), categoryData);
	} catch (error) {
		addError(`Error while uploading new Category to Firebase: ${error.message}`);
	}
}

export const uploadNewCategory = addCategory;

export const deleteCategory = async (categoryId, addError) => {
	const categoryRef = await fetchCategoryRef(categoryId, addError);
	try {
		await deleteDoc(categoryRef);
		return true;
	} catch (error) {
		addError(`Error while removing Category from Firebase: ${error.message}`);
		return false;
	}
}

export const addProfile = async (profile, addError) => {
	try {
		const profileName = String(profile?.profileName ?? "").trim().toUpperCase();
		if (!profileName) {
			addError("Profile name is required");
			return null;
		}
		if (profileName === RESERVED_PROFILE_NAME) {
			addError(`Profile "${RESERVED_PROFILE_NAME}" is reserved`);
			return null;
		}
		return await addDoc(collection(db, "profiles"), {profileName});
	} catch (error) {
		addError(`Error while adding new Profile to Firebase: ${error.message}`);
		return null;
	}
};

export const updateProfile = async (profileId, profileData, addError) => {
	try {
		if (!profileId || profileId === VIRTUAL_ALL_PROFILE_ID) {
			addError("Invalid profile ID for update");
			return false;
		}
		const profileName = String(profileData?.profileName ?? "").trim().toUpperCase();
		if (!profileName) {
			addError("Profile name is required");
			return false;
		}
		if (profileName === RESERVED_PROFILE_NAME) {
			addError(`Profile "${RESERVED_PROFILE_NAME}" is reserved`);
			return false;
		}
		const profileRef = doc(db, "profiles", profileId);
		await updateDoc(profileRef, {
			profileName
		});
		return true;
	} catch (error) {
		addError(`Error while updating Profile in Firebase: ${error.message}`);
		return false;
	}
};

export const fetchProfiles = async (addError) => {
	try {
		const querySnapshot = await getDocs(collection(db, "profiles"));
		return toArray(querySnapshot);
	} catch (error) {
		addError(`Error while getting Profiles from Firebase: ${error.message}`);
		return [];
	}
};

export const ensureDefaultProfiles = async (addError) => {
	const profiles = await fetchProfiles(addError);
	if (profiles.length > 0) return profiles;

	const defaults = ["WORK", "PERSONAL"];
	for (const profileName of defaults) {
		await addProfile({profileName}, addError);
	}
	return await fetchProfiles(addError);
};

export const deleteProfile = async (profileId, addError) => {
	try {
		if (!profileId || profileId === VIRTUAL_ALL_PROFILE_ID) {
			addError("Invalid profile ID for removal");
			return false;
		}
		await deleteDoc(doc(db, "profiles", profileId));
		return true;
	} catch (error) {
		addError(`Error while removing Profile from Firebase: ${error.message}`);
		return false;
	}
};

export const deleteProfileLifecycle = async (profileId, addError) => {
	if (!profileId || profileId === VIRTUAL_ALL_PROFILE_ID) {
		addError("Invalid profile ID for lifecycle deletion");
		return false;
	}

	try {
		const [linksSnapshot, categoriesSnapshot] = await Promise.all([
			getDocs(collection(db, "links")),
			getDocs(collection(db, "categories")),
		]);

		const batch = writeBatch(db);
		linksSnapshot.docs.forEach((snapshot) => {
			const link = {id: snapshot.id, ...snapshot.data()};
			const profileIds = getProfileIds(link);
			if (!profileIds.includes(profileId)) return;

			const remaining = profileIds.filter((id) => id !== profileId);
			if (remaining.length === 0) {
				batch.delete(snapshot.ref);
			} else {
				batch.update(snapshot.ref, {profileIds: remaining});
			}
		});

		categoriesSnapshot.docs.forEach((snapshot) => {
			const category = {id: snapshot.id, ...snapshot.data()};
			const profileIds = getProfileIds(category);
			if (!profileIds.includes(profileId)) return;

			const nextProfileIds = profileIds.filter((id) => id !== profileId);
			const nextIdxPerProfile = {...(category.idxPerProfile ?? {})};
			delete nextIdxPerProfile[profileId];

			const remainingRealProfiles = nextProfileIds.filter((id) => id !== VIRTUAL_ALL_PROFILE_ID);
			if (remainingRealProfiles.length === 0) {
				batch.delete(snapshot.ref);
			} else {
				batch.update(snapshot.ref, {
					profileIds: remainingRealProfiles,
					idxPerProfile: nextIdxPerProfile,
				});
			}
		});

		batch.delete(doc(db, "profiles", profileId));
		await batch.commit();
		return true;
	} catch (error) {
		addError(`Error while removing profile lifecycle in Firebase: ${error.message}`);
		return false;
	}
};

export const cleanUpDataBase = async (addError) => {
	if (!isDestructiveCleanupAllowed()) {
		addError('Database cleanup blocked: allowed only with emulator + explicit cleanup flag + demo project.');
		return false;
	}

	const links = await fetchLinks(addError);
	if (links?.length) {
		await Promise.all(links.map((link) => deleteLink(link.id, addError)));
	} else {
		addError(`No links found to be removed from Database`);
	}

	const categories = await fetchCategories(addError)
	if (categories?.length) {
		await Promise.all(categories.map((category) => deleteCategory(category.id, addError)));
	} else {
		addError(`No categories found to be removed from DataBase`);
	}

	return true;
}
